import { useCallback, useMemo } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ConfluencePageSubType } from '@confluence/create-blank-fabric-page/entry-points/CreateBlankPageMutation.ts';
import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils.ts';
// eslint-disable-next-line no-restricted-imports
import { useActivityFilterModalState } from '@confluence/feed/src/useActivityFilterModalState.ts';
// eslint-disable-next-line no-restricted-imports
import { useFollowingFeedWithSortByStorage } from '@confluence/feed/src/useFollowingFeedWithSortByStorage.ts';
import {
	useFeedTypeSelector,
	FeedType,
} from '@confluence/feed/entry-points/useFeedTypeSelector.ts';

import { usePTV2State } from '../../usePTV2Store.tsx';
import { fg } from '@confluence/feature-gating';
import { useNavigateToTeamsApp } from '@confluence/teams-app-config/entry-points/useNavigateToTeamsApp.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePublish } from '@atlaskit/rovo-triggers';

const SOURCE = 'nth-user-home-onboarding-revamp';

export function useActionHandlers(openActivityFilterModal: () => void): {
	handleActionClick: (id: string, complete: (id: string) => void) => void;
} {
	const { personalSpaceKey, onboardingPeriodDay } = usePTV2State();
	const { userId } = useSessionData();
	const publish = usePublish('ai-mate');
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { href: profilePictureHref } = useNavigateToTeamsApp({
		type: 'USER',
		payload: { userId: userId ?? '' },
	});

	const pageCreationLink = buildCreationLink({
		contentType: 'page',
		source: '`${SOURCE}--create-page`',
		spaceKey: personalSpaceKey,
		subType: ConfluencePageSubType.LIVE,
		openTemplatesGallery: true,
	});

	const whiteboardCreationLink = buildCreationLink({
		contentType: 'whiteboard',
		source: `${SOURCE}--create-whiteboard`,
		spaceKey: personalSpaceKey,
	});

	const hrefMap: Record<string, string> = useMemo(
		() => ({
			createPage: pageCreationLink,
			brainstormOnWhiteboard: whiteboardCreationLink,
			addProfilePicture: fg('confluence-adopt-teams-nav-config')
				? profilePictureHref
				: `/wiki/people/${userId}`,
		}),
		[pageCreationLink, whiteboardCreationLink, profilePictureHref, userId],
	);

	const handleActionClick = useCallback(
		(id: string, complete: (id: string) => void): void => {
			complete(id);

			if (id === 'meetAITeammate') {
				publish({
					type: 'chat-new',
					data: {
						name: 'Ask Rovo',
						dialogues: [],
						prompt: '',
						sourceId: `${SOURCE}--meet-ai-teammate`,
					},
					source: 'confluence',
					interactionSource: SOURCE,
					openChat: true,
				});
			} else if (id === 'followPeopleAndSpaces') {
				openActivityFilterModal();
			} else {
				// Handle href navigation for other actions
				const href = hrefMap[id];
				if (href) {
					const isExternalLink = href && !href.includes('wiki');
					if (isExternalLink) {
						window.open(href, '_blank');
					} else {
						window.location.href = href;
					}
				}
			}

			// Track analytics
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'gettingStartedCardNthUserHomeRevamp',
					source: SOURCE,
					attributes: {
						onboardingAction: id,
						onboardingPeriodDay,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, onboardingPeriodDay, openActivityFilterModal, hrefMap, publish],
	);

	return { handleActionClick };
}

export function useFeedHandlers(): {
	activityFilterModalOpen: boolean;
	closeActivityFilterModal: () => void;
	openActivityFilterModal: () => void;
	handleActivityFilterModalSave: () => void;
} {
	const { activityFilterModalOpen, closeActivityFilterModal, openActivityFilterModal } =
		useActivityFilterModalState();
	const { updateFeedType } = useFeedTypeSelector();
	const { followingFeedSortBy, followingFeedQuery: feedContentQuery } =
		useFollowingFeedWithSortByStorage();

	const handleFeedSelectorChange = useCallback(
		(updatedFeedType: FeedType | string) => {
			updateFeedType(updatedFeedType);
		},
		[updateFeedType],
	);

	const handleActivityFilterModalSave = useCallback((): void => {
		void feedContentQuery.refetch?.({ sortBy: followingFeedSortBy });
		handleFeedSelectorChange(FeedType.FOLLOWING);
	}, [feedContentQuery, followingFeedSortBy, handleFeedSelectorChange]);

	return {
		activityFilterModalOpen,
		closeActivityFilterModal,
		openActivityFilterModal,
		handleActivityFilterModalSave,
	};
}
